import { supabase } from '../supabase';

const DRAFT_FIELDS = 'id, slug, lang, kind, base_slug, title, summary, content, status, author_id, author_email, reviewer_email, review_notes, created_at, updated_at';

export function draftsEnabled() {
  return !!supabase;
}

/** All drafts authored by the current user. */
export async function listMyDrafts() {
  if (!supabase) return [];
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  const { data, error } = await supabase
    .from('wiki_drafts')
    .select(DRAFT_FIELDS)
    .eq('author_id', user.id)
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

/** Drafts awaiting review (admin only — RLS returns all rows for admins). */
export async function listDraftsForReview() {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('wiki_drafts')
    .select(DRAFT_FIELDS)
    .eq('status', 'submitted')
    .order('updated_at', { ascending: true });
  if (error) throw error;
  return data || [];
}

export async function getDraft(id) {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from('wiki_drafts')
    .select(DRAFT_FIELDS)
    .eq('id', id)
    .maybeSingle();
  if (error) throw error;
  return data;
}

export async function createDraft(fields) {
  if (!supabase) throw new Error('Supabase not configured');
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('You must be signed in to create a draft');
  const payload = {
    slug: fields.slug,
    lang: fields.lang || 'en',
    kind: fields.kind || 'edit',
    base_slug: fields.base_slug || null,
    title: fields.title || '',
    summary: fields.summary || '',
    content: fields.content || '',
    status: 'draft',
    author_id: user.id,
    author_email: user.email || null,
  };
  const { data, error } = await supabase
    .from('wiki_drafts')
    .insert(payload)
    .select(DRAFT_FIELDS)
    .single();
  if (error) throw error;
  return data;
}

export async function updateDraft(id, fields) {
  if (!supabase) throw new Error('Supabase not configured');
  const patch = { updated_at: new Date().toISOString() };
  ['slug', 'lang', 'kind', 'base_slug', 'title', 'summary', 'content', 'status', 'review_notes', 'reviewer_email']
    .forEach((k) => { if (fields[k] !== undefined) patch[k] = fields[k]; });
  const { data, error } = await supabase
    .from('wiki_drafts')
    .update(patch)
    .eq('id', id)
    .select(DRAFT_FIELDS)
    .single();
  if (error) throw error;
  return data;
}

export async function deleteDraft(id) {
  if (!supabase) throw new Error('Supabase not configured');
  const { error } = await supabase.from('wiki_drafts').delete().eq('id', id);
  if (error) throw error;
}

export async function submitDraft(id) {
  return updateDraft(id, { status: 'submitted' });
}

export async function rejectDraft(id, notes, reviewerEmail) {
  return updateDraft(id, { status: 'rejected', review_notes: notes || '', reviewer_email: reviewerEmail || null });
}

/** Approve a draft: publish it, record a revision, and mark the draft approved. */
export async function approveDraft(draft, reviewerEmail) {
  if (!supabase) throw new Error('Supabase not configured');
  const now = new Date().toISOString();
  const lang = draft.lang || 'en';
  const kind = draft.kind || 'edit';

  const publishRow = {
    slug: draft.slug,
    lang,
    kind,
    title: draft.title || '',
    summary: draft.summary || '',
    content: draft.content || '',
    source_draft_id: draft.id,
    author_email: draft.author_email || null,
    updated_by: reviewerEmail || null,
    updated_at: now,
  };
  const { error: pubErr } = await supabase
    .from('wiki_published')
    .upsert(publishRow, { onConflict: 'slug,lang' });
  if (pubErr) throw pubErr;

  // Append an immutable revision to the history log.
  const { error: revErr } = await supabase
    .from('wiki_revisions')
    .insert({
      slug: draft.slug,
      lang,
      kind,
      title: draft.title || '',
      summary: draft.summary || '',
      content: draft.content || '',
      author_email: draft.author_email || null,
      approved_by: reviewerEmail || null,
      source_draft_id: draft.id,
      created_at: now,
    });
  if (revErr) throw revErr;

  return updateDraft(draft.id, { status: 'approved', reviewer_email: reviewerEmail || null });
}

/** Edit history for a page (most recent first). */
export async function fetchHistory(slug, lang) {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from('wiki_revisions')
    .select('id, slug, lang, kind, title, summary, content, author_email, approved_by, created_at')
    .eq('slug', slug)
    .eq('lang', lang === 'zh' ? 'zh' : 'en')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return data || [];
}

/** Load all published overlay pages for a language, keyed by slug. */
export async function fetchPublishedOverlay(lang) {
  if (!supabase) return { bySlug: {}, newPages: [] };
  try {
    const { data, error } = await supabase
      .from('wiki_published')
      .select('slug, lang, kind, title, summary, content, author_email, updated_by, updated_at')
      .eq('lang', lang === 'zh' ? 'zh' : 'en');
    if (error) throw error;
    const bySlug = {};
    const newPages = [];
    (data || []).forEach((row) => {
      bySlug[row.slug] = row;
      if (row.kind === 'new') newPages.push({ slug: row.slug, title: row.title || row.slug });
    });
    return { bySlug, newPages };
  } catch {
    return { bySlug: {}, newPages: [] };
  }
}
